/**
 * Geri Çağırma (Recalls & Returns) — Prompt 3 iter78.
 *
 * 3 sekme:
 *   1. Parti/Lot Geri Çağırma — forward/backward izlenebilirlik + Etki Analizi + Mock Recall
 *   2. Müşteri İadeleri — RETURN QUARANTINE, QC kararı bekler
 *   3. Tedarikçi İadeleri — ham madde iade, fatura/refund/replacement
 *
 * UX kuralı: "LİSTE SADE, DETAY ZENGİN" — ana tablolarda 6-7 kolon.
 * Detay Modal içerisinde zengin veri. Mali Analiz sadece System Admin.
 */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, Card, Btn, Badge, Modal, Input, Select, Textarea, Table } from "@/components/UI";
import { todayTR } from "@/lib/date";
import GmpEvidencePanel from "@/components/GmpEvidencePanel";
import {
  ShieldAlert, Search, AlertTriangle, ArrowLeftRight, PackageOpen, Truck,
  Factory, ShoppingCart, ClipboardCheck, PlayCircle, XCircle, CheckCircle,
  Package, Users2, RefreshCcw, DollarSign, Lock,
} from "lucide-react";

const fmtDt = (s) => {
  if (!s) return "—";
  try { return new Date(s).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" }); }
  catch { return s; }
};
const fmtD = (s) => {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString("tr-TR"); }
  catch { return s; }
};

/* ============================================================
   TOP-LEVEL PAGE
   ============================================================ */
export default function RecallsPage() {
  const [tab, setTab] = useState("lot");
  const TABS = [
    { key: "lot", label: "Parti/Lot Geri Çağırma", icon: ShieldAlert },
    { key: "customer", label: "Müşteri İadeleri", icon: PackageOpen },
    { key: "supplier", label: "Tedarikçi İadeleri", icon: Truck },
  ];
  return (
    <div>
      <PageHeader
        title="Geri Çağırma"
        description="Parti/Lot geri çağırma · Müşteri iadeleri · Tedarikçi iadeleri"
      />
      <div className="mb-4 flex flex-wrap items-center gap-1 border-b border-slate-200 dark:border-slate-700">
        {TABS.map(t => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`recall-tab-${t.key}`}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition -mb-px ${
                active ? "border-rose-600 text-rose-700 dark:text-rose-300"
                       : "border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"}`}>
              <Icon size={14}/> {t.label}
            </button>
          );
        })}
      </div>
      {tab === "lot" && <LotRecallTab/>}
      {tab === "customer" && <CustomerReturnsTab/>}
      {tab === "supplier" && <SupplierReturnsTab/>}
    </div>
  );
}

/* ============================================================
   TAB 1 — Parti/Lot Geri Çağırma
   ============================================================ */
function LotRecallTab() {
  const [q, setQ] = useState("");
  const [searchRes, setSearchRes] = useState([]);
  const [selectedLots, setSelectedLots] = useState([]);
  const [impact, setImpact] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState(""); // "mock" | "real"
  const [reason, setReason] = useState("");
  const [severity, setSeverity] = useState("warning");
  const [actionNotes, setActionNotes] = useState("");
  const [events, setEvents] = useState([]);
  const [openDetail, setOpenDetail] = useState(null);

  const loadEvents = useCallback(() => {
    api.get("/recalls").then(setEvents).catch(() => setEvents([]));
  }, []);
  useEffect(() => { loadEvents(); }, [loadEvents]);

  const searchLots = async () => {
    setBusy(true);
    try {
      const rs = await api.get(`/recalls/lot-search?q=${encodeURIComponent(q || "")}`);
      setSearchRes(rs || []);
    } catch (e) { alert(e.response?.data?.detail || "Arama başarısız"); }
    finally { setBusy(false); }
  };

  const toggleLot = (lot) => {
    setSelectedLots(prev => prev.some(x => x.id === lot.id)
      ? prev.filter(x => x.id !== lot.id)
      : [...prev, lot]);
  };

  const runMock = async () => {
    if (selectedLots.length === 0) { alert("En az bir lot seçmelisiniz"); return; }
    setBusy(true); setMode("mock");
    try {
      const res = await api.post("/recalls/mock", {
        lot_ids: selectedLots.map(l => l.id),
        reason: reason || "Mock simülasyon",
      });
      setImpact(res);
    } catch (e) { alert(e.response?.data?.detail || "Mock recall başarısız"); }
    finally { setBusy(false); }
  };

  const runReal = async () => {
    if (selectedLots.length === 0) { alert("En az bir lot seçmelisiniz"); return; }
    if (!reason.trim()) { alert("Geri çağırma nedeni zorunludur"); return; }
    if (!window.confirm(`GERÇEK GERİ ÇAĞIRMA ÇALIŞTIRILACAK — ${selectedLots.length} lot etkilenecek. Devam edilsin mi?`)) return;
    setBusy(true); setMode("real");
    try {
      const res = await api.post("/recalls/execute", {
        lot_ids: selectedLots.map(l => l.id),
        reason: reason.trim(),
        severity,
        action_notes: actionNotes,
      });
      alert(`Geri çağırma tamamlandı: ${res.event_no}`);
      setSelectedLots([]); setImpact(null); setReason(""); setActionNotes("");
      loadEvents();
    } catch (e) { alert(e.response?.data?.detail || "Recall başarısız"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      {/* LOT ARAMA + SEÇİM */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Search size={16} className="text-rose-500"/>
          <input
            value={q}
            onChange={e => setQ(e.target.value)}
            onKeyDown={e => e.key === "Enter" && searchLots()}
            placeholder="Lot No / Tedarikçi Lot / Malzeme adı..."
            data-testid="recall-lot-search"
            className="flex-1 px-3 py-1.5 text-sm border rounded-md bg-white dark:bg-slate-800/70 border-slate-200 dark:border-slate-700"
          />
          <Btn onClick={searchLots} disabled={busy} data-testid="recall-lot-search-btn">Ara</Btn>
        </div>
        {searchRes.length > 0 && (
          <div className="max-h-52 overflow-auto border border-slate-200 dark:border-slate-700 rounded">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 dark:bg-slate-800/50 sticky top-0">
                <tr>
                  <th className="text-left px-2 py-1.5 font-semibold">Seç</th>
                  <th className="text-left px-2 py-1.5 font-semibold">Lot Kodu</th>
                  <th className="text-left px-2 py-1.5 font-semibold">Malzeme</th>
                  <th className="text-left px-2 py-1.5 font-semibold">Tedarikçi</th>
                  <th className="text-right px-2 py-1.5 font-semibold">Mevcut</th>
                  <th className="text-left px-2 py-1.5 font-semibold">Durum</th>
                </tr>
              </thead>
              <tbody>
                {searchRes.map(l => {
                  const checked = selectedLots.some(x => x.id === l.id);
                  return (
                    <tr key={l.id} className="border-t border-slate-100 dark:border-slate-700 hover:bg-rose-50/40 dark:hover:bg-rose-500/10 cursor-pointer" onClick={() => toggleLot(l)}>
                      <td className="px-2 py-1"><input type="checkbox" checked={checked} readOnly data-testid={`recall-select-${l.id}`}/></td>
                      <td className="px-2 py-1 font-mono text-[11px]">{l.lot_code}</td>
                      <td className="px-2 py-1">{l.material_name}</td>
                      <td className="px-2 py-1 text-slate-500">{l.supplier || "—"}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{l.qty_available} {l.unit}</td>
                      <td className="px-2 py-1"><Badge level={l.status === "approved" ? "ok" : "attention"}>{l.status}</Badge></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* SEÇİLİ LOTLAR + AKSİYON */}
      {selectedLots.length > 0 && (
        <Card className="p-4 border-rose-200 dark:border-rose-500/40 bg-rose-50/40 dark:bg-rose-500/5">
          <div className="text-sm font-semibold mb-2">Seçilen Lotlar ({selectedLots.length})</div>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {selectedLots.map(l => (
              <span key={l.id} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-rose-100 dark:bg-rose-500/20 text-rose-800 dark:text-rose-200 border border-rose-300 dark:border-rose-500/40" data-testid={`recall-chip-${l.id}`}>
                {l.lot_code}
                <button onClick={() => toggleLot(l)} className="hover:text-rose-900 dark:hover:text-white">×</button>
              </span>
            ))}
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            <Input label="Neden *" value={reason} onChange={e => setReason(e.target.value)} placeholder="Kontaminasyon, spesifikasyon dışı..." data-testid="recall-reason"/>
            <Select label="Şiddet" value={severity} onChange={e => setSeverity(e.target.value)} data-testid="recall-severity">
              <option value="info">Bilgi</option>
              <option value="warning">Uyarı</option>
              <option value="critical">Kritik</option>
            </Select>
            <Textarea label="Aksiyon Notları" value={actionNotes} onChange={e => setActionNotes(e.target.value)} placeholder="Alınan aksiyonlar, iletişim planı..." data-testid="recall-action-notes"/>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <Btn variant="secondary" onClick={runMock} disabled={busy} data-testid="recall-run-mock">
              <PlayCircle size={14}/> Mock Simülasyon (DB'ye dokunmaz)
            </Btn>
            <Btn variant="danger" onClick={runReal} disabled={busy} data-testid="recall-run-real">
              <AlertTriangle size={14}/> GERÇEK Recall Çalıştır
            </Btn>
          </div>
        </Card>
      )}

      {/* ETKİ ANALİZİ (mock veya gerçek sonrası) */}
      {impact && (
        <ImpactAnalysisPanel impact={impact} mode={mode} onClose={() => setImpact(null)}/>
      )}

      {/* GEÇMİŞ RECALL LİSTESİ — sade */}
      <Card className="p-0 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-800/40">
          <div className="text-sm font-semibold">Geçmiş Geri Çağırmalar</div>
          <button onClick={loadEvents} className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"><RefreshCcw size={12}/> Yenile</button>
        </div>
        <Table testid="recalls-table"
          columns={[
            { label: "Kod", render: r => <span className="font-mono text-xs">{r.event_no}</span> },
            { label: "Tarih", render: r => fmtDt(r.created_at) },
            { label: "Neden", render: r => <span className="truncate max-w-[200px] inline-block">{r.reason}</span> },
            { label: "Şiddet", render: r => <Badge level={r.severity === "critical" ? "missing" : r.severity === "warning" ? "attention" : "ok"}>{r.severity}</Badge> },
            { label: "Lot", render: r => r.lot_count },
            { label: "Müşteri", render: r => r.affected_customers },
            { label: "İşlem", render: r => <Btn size="sm" variant="secondary" onClick={() => setOpenDetail(r)} data-testid={`recall-detail-${r.id}`}>Detay</Btn> },
          ]} rows={events}/>
      </Card>

      {openDetail && (
        <Modal open={true} onClose={() => setOpenDetail(null)}
          title={`Geri Çağırma — ${openDetail.event_no}`} size="full">
          <RecallEventDetail eventId={openDetail.id}/>
        </Modal>
      )}
    </div>
  );
}

function ImpactAnalysisPanel({ impact, mode, onClose }) {
  return (
    <Card className="p-4 border-2 border-amber-300 dark:border-amber-500/50 bg-amber-50/40 dark:bg-amber-500/5">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-semibold flex items-center gap-2">
          <AlertTriangle size={16} className="text-amber-600"/> Etki Analizi
          {mode === "mock" && <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200">MOCK — DB'YE DOKUNULMADI</span>}
          {mode === "real" && <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-500 text-white">GERÇEK RECALL</span>}
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl leading-none">×</button>
      </div>
      <ImpactBody impact={impact}/>
    </Card>
  );
}

function ImpactBody({ impact }) {
  return (
    <div className="space-y-4 text-sm">
      <div className="grid md:grid-cols-4 gap-2">
        <StatCard icon={Package} label="Etkilenen Lot" value={impact.lots?.length || 0}/>
        <StatCard icon={Factory} label="Üretim Emri" value={impact.production_orders?.length || 0}/>
        <StatCard icon={Users2} label="Müşteri" value={impact.customers?.length || 0}/>
        <StatCard icon={Truck} label="Sevkiyat" value={impact.shipments?.length || 0}/>
      </div>

      {/* Backward */}
      <ImpactSection icon={ArrowLeftRight} title="Geriye Doğru İzleme (Tedarikçi)" testid="impact-backward">
        {(impact.backward || []).length === 0 ? <Empty/> :
          <table className="w-full text-xs">
            <thead className="bg-slate-50 dark:bg-slate-800/60"><tr>
              <th className="text-left px-2 py-1.5">Lot</th><th className="text-left px-2 py-1.5">Malzeme</th>
              <th className="text-left px-2 py-1.5">Tedarikçi</th><th className="text-left px-2 py-1.5">PO No</th>
              <th className="text-left px-2 py-1.5">Sipariş Tarihi</th>
            </tr></thead>
            <tbody>
              {impact.backward.map((b, i) => (
                <tr key={i} className="border-t border-slate-100 dark:border-slate-700">
                  <td className="px-2 py-1 font-mono">{b.lot_code}</td>
                  <td className="px-2 py-1">{b.material_name}</td>
                  <td className="px-2 py-1">{b.supplier_name}</td>
                  <td className="px-2 py-1 font-mono">{b.po_no}</td>
                  <td className="px-2 py-1">{fmtD(b.ordered_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>}
      </ImpactSection>

      {/* Production */}
      <ImpactSection icon={Factory} title="İleriye Doğru — Üretim" testid="impact-production">
        {(impact.production_orders || []).length === 0 ? <Empty/> :
          <table className="w-full text-xs">
            <thead className="bg-slate-50 dark:bg-slate-800/60"><tr>
              <th className="text-left px-2 py-1.5">Üretim No</th><th className="text-left px-2 py-1.5">Müşteri</th>
              <th className="text-left px-2 py-1.5">Ürün</th><th className="text-right px-2 py-1.5">Adet</th>
              <th className="text-left px-2 py-1.5">Durum</th><th className="text-left px-2 py-1.5">Kullanılan Lotlar</th>
            </tr></thead>
            <tbody>
              {impact.production_orders.map((p, i) => (
                <tr key={i} className="border-t border-slate-100 dark:border-slate-700">
                  <td className="px-2 py-1 font-mono">{p.prod_no}</td>
                  <td className="px-2 py-1">{p.customer_name}</td>
                  <td className="px-2 py-1">{p.product_name}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{p.planned_units}</td>
                  <td className="px-2 py-1"><Badge level={p.status === "completed" ? "ok" : "attention"}>{p.status}</Badge></td>
                  <td className="px-2 py-1 font-mono text-[10px]">{(p.matched_lots || []).map(m => `${m.lot_code} (${m.used_qty})`).join(", ")}</td>
                </tr>
              ))}
            </tbody>
          </table>}
      </ImpactSection>

      {/* Customers */}
      <ImpactSection icon={Users2} title="Etkilenen Müşteriler" testid="impact-customers">
        {(impact.customers || []).length === 0 ? <Empty/> :
          <table className="w-full text-xs">
            <thead className="bg-slate-50 dark:bg-slate-800/60"><tr>
              <th className="text-left px-2 py-1.5">Müşteri</th><th className="text-right px-2 py-1.5">Toplam Sevk</th>
              <th className="text-left px-2 py-1.5">Sevkiyatlar</th>
            </tr></thead>
            <tbody>
              {impact.customers.map((c, i) => (
                <tr key={i} className="border-t border-slate-100 dark:border-slate-700">
                  <td className="px-2 py-1 font-semibold">{c.customer_name}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{c.total_qty}</td>
                  <td className="px-2 py-1 text-slate-600 dark:text-slate-400">
                    {c.note ? <em>{c.note}</em>
                            : (c.shipments || []).map(s => `${s.dispatch_no} (${s.qty})`).join(" · ")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>}
      </ImpactSection>

      {/* Inventory now */}
      <ImpactSection icon={Package} title="Envanterde Kalan Miktar" testid="impact-inventory">
        {(impact.inventory_now || []).length === 0 ? <Empty text="Bu lot(lar) envanterde tükenmiş"/> :
          <table className="w-full text-xs">
            <thead className="bg-slate-50 dark:bg-slate-800/60"><tr>
              <th className="text-left px-2 py-1.5">Lot</th><th className="text-left px-2 py-1.5">Malzeme</th>
              <th className="text-right px-2 py-1.5">Miktar</th>
            </tr></thead>
            <tbody>
              {impact.inventory_now.map((l, i) => (
                <tr key={i} className="border-t border-slate-100 dark:border-slate-700">
                  <td className="px-2 py-1 font-mono">{l.lot_code}</td>
                  <td className="px-2 py-1">{l.material_name}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{l.qty_available} {l.unit}</td>
                </tr>
              ))}
            </tbody>
          </table>}
      </ImpactSection>
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-800/50 p-3 flex items-center gap-2">
      <Icon size={18} className="text-rose-500"/>
      <div>
        <div className="text-[10px] uppercase text-slate-500">{label}</div>
        <div className="text-lg font-bold tabular-nums">{value}</div>
      </div>
    </div>
  );
}

function ImpactSection({ icon: Icon, title, testid, children }) {
  return (
    <div data-testid={testid}>
      <div className="text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wide mb-1 flex items-center gap-1.5">
        <Icon size={12}/> {title}
      </div>
      <div className="border border-slate-200 dark:border-slate-700 rounded-md overflow-x-auto bg-white/70 dark:bg-slate-800/40">
        {children}
      </div>
    </div>
  );
}

function Empty({ text = "Kayıt yok" }) {
  return <div className="text-xs text-slate-400 dark:text-slate-500 text-center py-4">{text}</div>;
}

function RecallEventDetail({ eventId }) {
  const [doc, setDoc] = useState(null);
  useEffect(() => {
    api.get(`/recalls/${eventId}`).then(setDoc).catch(() => setDoc(null));
  }, [eventId]);
  if (!doc) return <div className="text-center text-slate-400 py-8">Yükleniyor...</div>;
  return (
    <div className="space-y-3">
      <div className="grid md:grid-cols-2 gap-3 text-sm">
        <KV label="Kod" v={doc.event_no}/>
        <KV label="Tarih" v={fmtDt(doc.created_at)}/>
        <KV label="Kullanıcı" v={doc.created_by_name}/>
        <KV label="Şiddet" v={<Badge level={doc.severity === "critical" ? "missing" : "attention"}>{doc.severity}</Badge>}/>
        <div className="md:col-span-2"><KV label="Neden" v={doc.reason}/></div>
        {doc.action_notes && <div className="md:col-span-2"><KV label="Aksiyon" v={doc.action_notes}/></div>}
      </div>
      <ImpactBody impact={doc.impact_snapshot || {}}/>
      <GmpEvidencePanel module="recall" recordId={doc.id}/>
    </div>
  );
}

function KV({ label, v }) {
  return (
    <div>
      <div className="text-[10px] uppercase text-slate-500 tracking-wider">{label}</div>
      <div className="text-sm text-slate-800 dark:text-slate-100">{v || "—"}</div>
    </div>
  );
}

/* ============================================================
   TAB 2 — Müşteri İadeleri
   ============================================================ */
function CustomerReturnsTab() {
  const [rows, setRows] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [openDetail, setOpenDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get("/customer-returns").then(setRows).catch(() => setRows([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end gap-2">
        <Btn onClick={() => setOpenNew(true)} data-testid="cr-new-btn">
          <PackageOpen size={14}/> Yeni Müşteri İadesi
        </Btn>
      </div>
      <div className="rounded-lg bg-amber-50/60 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 px-3 py-2 text-xs text-amber-800 dark:text-amber-200 flex items-center gap-2">
        <Lock size={12}/> Müşteri iadesi <b>satılabilir stoğa direkt eklenmez</b> — Karantina statüsünde QC kararı bekler.
      </div>
      <Card className="p-0 overflow-hidden">
        <Table testid="customer-returns-table"
          columns={[
            { label: "Kod", render: r => <span className="font-mono text-xs">{r.return_no}</span> },
            { label: "Tarih", render: r => fmtD(r.return_date || r.created_at) },
            { label: "Müşteri", render: r => r.customer_name || "—" },
            { label: "Ürün", render: r => r.product_name || "—" },
            { label: "Miktar", render: r => <span className="tabular-nums">{r.qty_returned}</span> },
            { label: "Durum", render: r => <StatusBadge status={r.status}/> },
            { label: "İşlem", render: r => (
              <Btn size="sm" variant="secondary" onClick={() => setOpenDetail(r)} data-testid={`cust-return-detail-${r.id}`}>Detay</Btn>
            )},
          ]} rows={rows}/>
      </Card>

      {openNew && <NewCustomerReturnModal onClose={() => setOpenNew(false)} onSaved={() => { setOpenNew(false); load(); }}/>}
      {openDetail && <CustomerReturnDetailModal id={openDetail.id} onClose={() => setOpenDetail(null)} onChanged={load}/>}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    quarantine: { l: "Karantina", level: "attention" },
    sellable: { l: "Satılabilir", level: "ok" },
    scrap: { l: "İmha", level: "missing" },
    rework: { l: "Yeniden İşleme", level: "attention" },
    pending: { l: "Beklemede", level: "attention" },
    invoice_issued: { l: "Fatura Kesildi", level: "attention" },
    refunded: { l: "İade Edildi", level: "ok" },
    replaced: { l: "Yerine Gönderildi", level: "ok" },
  };
  const m = map[status] || { l: status || "—", level: "inactive" };
  return <Badge level={m.level}>{m.l}</Badge>;
}

function NewCustomerReturnModal({ onClose, onSaved }) {
  const [salesOrders, setSalesOrders] = useState([]);
  const [finishedLots, setFinishedLots] = useState([]);
  const [f, setF] = useState({
    sales_order_id: "", finished_lot_id: "", qty_returned: "",
    return_date: todayTR(),
    reason: "", condition_notes: "", carrier: "",
  });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/sales-orders").then(setSalesOrders).catch(() => {});
    api.get("/finished-lots").then(setFinishedLots).catch(() => setFinishedLots([]));
  }, []);
  const lotsForSo = useMemo(() => {
    if (!f.sales_order_id) return finishedLots;
    // filter finished_lots to those tied to selected SO
    const so = salesOrders.find(s => s.id === f.sales_order_id);
    if (!so) return finishedLots;
    return finishedLots.filter(l => l.sales_order_id === f.sales_order_id
      || (so.lines || []).some(ln => ln.product_id === l.product_id));
  }, [f.sales_order_id, finishedLots, salesOrders]);

  const save = async () => {
    if (!f.finished_lot_id) return alert("Bitmiş ürün lotu zorunludur");
    if (!f.qty_returned || Number(f.qty_returned) <= 0) return alert("Miktar 0'dan büyük olmalı");
    if (!f.reason.trim()) return alert("İade nedeni zorunludur");
    setBusy(true);
    try {
      await api.post("/customer-returns", { ...f, qty_returned: Number(f.qty_returned) });
      onSaved();
    } catch (e) { alert(e.response?.data?.detail || "Kaydedilemedi"); }
    finally { setBusy(false); }
  };
  return (
    <Modal open={true} onClose={onClose} title="Yeni Müşteri İadesi" size="lg">
      <div className="grid md:grid-cols-2 gap-3">
        <Select label="Satış Siparişi (opsiyonel)" value={f.sales_order_id} onChange={e => setF({ ...f, sales_order_id: e.target.value, finished_lot_id: "" })} data-testid="cr-sales-order">
          <option value="">— Seçmeden Devam Et —</option>
          {salesOrders.map(s => (
            <option key={s.id} value={s.id}>{s.order_no} · {s.customer_name || s.customer_id?.slice(0,8)}</option>
          ))}
        </Select>
        <Select label="Bitmiş Ürün Lotu *" value={f.finished_lot_id} onChange={e => setF({ ...f, finished_lot_id: e.target.value })} data-testid="cr-finished-lot">
          <option value="">— Lot Seçin —</option>
          {lotsForSo.map(l => (
            <option key={l.id} value={l.id}>{l.lot_code} · {l.product_name || l.product_id?.slice(0,8)}</option>
          ))}
        </Select>
        <Input label="Miktar *" type="number" step="0.01" value={f.qty_returned} onChange={e => setF({ ...f, qty_returned: e.target.value })} data-testid="cr-qty"/>
        <Input label="İade Tarihi" type="date" value={f.return_date} onChange={e => setF({ ...f, return_date: e.target.value })} data-testid="cr-date"/>
        <div className="md:col-span-2"><Input label="İade Nedeni *" value={f.reason} onChange={e => setF({ ...f, reason: e.target.value })} placeholder="Hasar, spesifikasyon dışı, müşteri talebi..." data-testid="cr-reason"/></div>
        <div className="md:col-span-2"><Textarea label="Ürün Durumu / Notlar" value={f.condition_notes} onChange={e => setF({ ...f, condition_notes: e.target.value })} data-testid="cr-condition"/></div>
        <Input label="Taşıyıcı" value={f.carrier} onChange={e => setF({ ...f, carrier: e.target.value })} data-testid="cr-carrier"/>
      </div>
      <div className="mt-3 rounded-md bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 px-3 py-2 text-xs text-amber-800 dark:text-amber-200">
        <Lock size={11} className="inline mr-1"/> Bu iade otomatik olarak <b>RETURN QUARANTINE</b> statüsüne alınacaktır. Ürün QC kararı verilene kadar satılabilir stoğa dâhil edilmez.
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <Btn variant="secondary" onClick={onClose}>Vazgeç</Btn>
        <Btn onClick={save} disabled={busy} data-testid="cr-save">Kaydet</Btn>
      </div>
    </Modal>
  );
}

function CustomerReturnDetailModal({ id, onClose, onChanged }) {
  const { user } = useAuth();
  const [doc, setDoc] = useState(null);
  const [decision, setDecision] = useState("sellable");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const isAdmin = user?.role === "admin";

  const load = useCallback(() => {
    api.get(`/customer-returns/${id}`).then(setDoc).catch(() => setDoc(null));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const decide = async () => {
    if (!window.confirm(`QC kararı '${decision}' olarak kaydedilsin mi?`)) return;
    setBusy(true);
    try {
      await api.post(`/customer-returns/${id}/qc-decide`, { decision, notes });
      onChanged?.(); load();
    } catch (e) { alert(e.response?.data?.detail || "Kaydedilemedi"); }
    finally { setBusy(false); }
  };

  if (!doc) return <Modal open={true} onClose={onClose} title="İade Detayı" size="lg"><div className="text-center py-8 text-slate-400">Yükleniyor...</div></Modal>;
  const fi = doc.financial_impact;

  return (
    <Modal open={true} onClose={onClose} title={`Müşteri İadesi — ${doc.return_no}`} size="xl">
      <div className="space-y-4">
        <div className="grid md:grid-cols-3 gap-3 text-sm">
          <KV label="Durum" v={<StatusBadge status={doc.status}/>}/>
          <KV label="Müşteri" v={doc.customer_name}/>
          <KV label="Ürün" v={doc.product_name}/>
          <KV label="Sipariş No" v={doc.sales_order_no}/>
          <KV label="Bitmiş Lot" v={doc.finished_lot_code}/>
          <KV label="Miktar" v={doc.qty_returned}/>
          <KV label="İade Tarihi" v={fmtD(doc.return_date)}/>
          <KV label="Taşıyıcı" v={doc.carrier}/>
          <KV label="Kaydı Alan" v={doc.created_by_name}/>
          <div className="md:col-span-3"><KV label="Neden" v={doc.reason}/></div>
          {doc.condition_notes && <div className="md:col-span-3"><KV label="Ürün Durumu" v={doc.condition_notes}/></div>}
        </div>

        {/* MALİ ANALİZ — SADECE SİSTEM YÖNETİCİSİ */}
        <div className="rounded-lg border-2 border-dashed border-emerald-300 dark:border-emerald-500/40 p-3" data-testid="cr-financial-section">
          <div className="text-xs font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-300 mb-2 flex items-center gap-1.5">
            <DollarSign size={12}/> MALİ ANALİZ
            {!isAdmin && <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 inline-flex items-center gap-1"><Lock size={10}/> Sadece Sistem Yöneticisi</span>}
          </div>
          {isAdmin && fi ? (
            <div className="grid md:grid-cols-4 gap-3 text-sm">
              <KV label="Birim Fiyat" v={<span className="tabular-nums">{fi.unit_price} {fi.currency}</span>}/>
              <KV label="Miktar" v={<span className="tabular-nums">{fi.qty}</span>}/>
              <KV label="İade Tutarı" v={<span className="tabular-nums font-bold text-rose-600">{fi.refund_amount} {fi.currency}</span>}/>
              <KV label="Para Birimi" v={fi.currency}/>
            </div>
          ) : (
            <div className="text-xs text-slate-400 italic">Bu bilgiler yalnızca Sistem Yöneticisi tarafından görüntülenebilir.</div>
          )}
        </div>

        {/* QC KARARI */}
        {doc.status === "quarantine" ? (
          <div className="rounded-lg border-2 border-amber-300 dark:border-amber-500/50 p-3 bg-amber-50/40 dark:bg-amber-500/5">
            <div className="text-xs font-semibold uppercase tracking-wider text-amber-800 dark:text-amber-200 mb-2 flex items-center gap-1.5">
              <ClipboardCheck size={12}/> QC Kararı Bekleniyor
            </div>
            <div className="grid md:grid-cols-3 gap-3 mb-3">
              <Select label="Karar" value={decision} onChange={e => setDecision(e.target.value)} data-testid="cr-qc-decision">
                <option value="sellable">Satılabilir — Envantere Ekle</option>
                <option value="scrap">İmha — Kayıp</option>
                <option value="rework">Yeniden İşle</option>
              </Select>
              <div className="md:col-span-2"><Textarea label="QC Notları" value={notes} onChange={e => setNotes(e.target.value)} data-testid="cr-qc-notes"/></div>
            </div>
            <div className="flex justify-end">
              <Btn variant="approve" onClick={decide} disabled={busy} data-testid="cr-qc-submit"><CheckCircle size={14}/> QC Kararını Kaydet</Btn>
            </div>
          </div>
        ) : (
          <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 bg-slate-50/60 dark:bg-slate-800/40">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300 mb-2 flex items-center gap-1.5">
              <ClipboardCheck size={12}/> QC Kararı
            </div>
            <div className="grid md:grid-cols-3 gap-3 text-sm">
              <KV label="Karar" v={<StatusBadge status={doc.qc_decision}/>}/>
              <KV label="Karar Veren" v={doc.qc_decided_by_name}/>
              <KV label="Karar Tarihi" v={fmtDt(doc.qc_decided_at)}/>
              {doc.qc_notes && <div className="md:col-span-3"><KV label="Notlar" v={doc.qc_notes}/></div>}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

/* ============================================================
   TAB 3 — Tedarikçi İadeleri
   ============================================================ */
function SupplierReturnsTab() {
  const [rows, setRows] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [openDetail, setOpenDetail] = useState(null);
  const load = useCallback(() => {
    api.get("/supplier-returns").then(setRows).catch(() => setRows([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end gap-2">
        <Btn onClick={() => setOpenNew(true)} data-testid="sr-new-btn">
          <Truck size={14}/> Yeni Tedarikçi İadesi
        </Btn>
      </div>
      <Card className="p-0 overflow-hidden">
        <Table testid="supplier-returns-table"
          columns={[
            { label: "Kod", render: r => <span className="font-mono text-xs">{r.return_no}</span> },
            { label: "Tarih", render: r => fmtD(r.return_date || r.created_at) },
            { label: "Tedarikçi", render: r => r.supplier_name || "—" },
            { label: "Malzeme", render: r => r.material_name || "—" },
            { label: "Miktar", render: r => <span className="tabular-nums">{r.qty_returned} {r.unit || ""}</span> },
            { label: "Durum", render: r => <StatusBadge status={r.status}/> },
            { label: "İşlem", render: r => (
              <Btn size="sm" variant="secondary" onClick={() => setOpenDetail(r)} data-testid={`sr-detail-${r.id}`}>Detay</Btn>
            )},
          ]} rows={rows}/>
      </Card>
      {openNew && <NewSupplierReturnModal onClose={() => setOpenNew(false)} onSaved={() => { setOpenNew(false); load(); }}/>}
      {openDetail && <SupplierReturnDetailModal id={openDetail.id} onClose={() => setOpenDetail(null)} onChanged={load}/>}
    </div>
  );
}

function NewSupplierReturnModal({ onClose, onSaved }) {
  const [q, setQ] = useState("");
  const [lots, setLots] = useState([]);
  const [selectedLot, setSelectedLot] = useState(null);
  const [f, setF] = useState({
    qty_returned: "", reason: "", resolution_type: "refund",
    return_date: todayTR(), notes: "",
  });
  const [busy, setBusy] = useState(false);

  const searchLots = async () => {
    try {
      const r = await api.get(`/recalls/lot-search?q=${encodeURIComponent(q || "")}`);
      setLots(r || []);
    } catch (e) {}
  };
  useEffect(() => { searchLots(); }, []);

  const save = async () => {
    if (!selectedLot) return alert("Bir lot seçmelisiniz");
    if (!f.qty_returned || Number(f.qty_returned) <= 0) return alert("Miktar 0'dan büyük olmalı");
    if (!f.reason.trim()) return alert("İade nedeni zorunludur");
    setBusy(true);
    try {
      await api.post("/supplier-returns", { ...f, lot_id: selectedLot.id, qty_returned: Number(f.qty_returned) });
      onSaved();
    } catch (e) { alert(e.response?.data?.detail || "Kaydedilemedi"); }
    finally { setBusy(false); }
  };

  return (
    <Modal open={true} onClose={onClose} title="Yeni Tedarikçi İadesi" size="lg">
      <div className="space-y-3">
        <div>
          <div className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">Lot Ara</div>
          <div className="flex gap-2">
            <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === "Enter" && searchLots()}
              placeholder="Lot / Tedarikçi..." className="flex-1 px-2.5 py-1.5 text-sm border rounded-md bg-white dark:bg-slate-800/70 border-slate-200 dark:border-slate-700"
              data-testid="sr-lot-search"/>
            <Btn onClick={searchLots} variant="secondary" data-testid="sr-lot-search-btn">Ara</Btn>
          </div>
          {lots.length > 0 && (
            <div className="mt-2 max-h-40 overflow-auto border border-slate-200 dark:border-slate-700 rounded">
              {lots.map(l => (
                <button key={l.id} onClick={() => setSelectedLot(l)}
                  data-testid={`sr-lot-pick-${l.id}`}
                  className={`w-full text-left px-2 py-1 text-xs border-b last:border-0 border-slate-100 dark:border-slate-700 hover:bg-rose-50 dark:hover:bg-rose-500/10 ${selectedLot?.id === l.id ? "bg-rose-100 dark:bg-rose-500/20 font-semibold" : ""}`}>
                  <span className="font-mono">{l.lot_code}</span> · {l.material_name} · {l.supplier || "—"} · Mevcut: {l.qty_available} {l.unit}
                </button>
              ))}
            </div>
          )}
        </div>
        {selectedLot && (
          <div className="rounded-md bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 p-2 text-xs">
            Seçilen: <b>{selectedLot.lot_code}</b> · {selectedLot.material_name} · Mevcut {selectedLot.qty_available} {selectedLot.unit}
          </div>
        )}
        <div className="grid md:grid-cols-2 gap-3">
          <Input label="İade Miktarı *" type="number" step="0.01" value={f.qty_returned} onChange={e => setF({ ...f, qty_returned: e.target.value })} data-testid="sr-qty"/>
          <Input label="İade Tarihi" type="date" value={f.return_date} onChange={e => setF({ ...f, return_date: e.target.value })} data-testid="sr-date"/>
          <Select label="Çözüm Tipi" value={f.resolution_type} onChange={e => setF({ ...f, resolution_type: e.target.value })} data-testid="sr-resolution">
            <option value="refund">İade / Refund</option>
            <option value="replacement">Yerine Gönderim</option>
          </Select>
          <div className="md:col-span-2"><Input label="İade Nedeni *" value={f.reason} onChange={e => setF({ ...f, reason: e.target.value })} data-testid="sr-reason"/></div>
          <div className="md:col-span-2"><Textarea label="Notlar" value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} data-testid="sr-notes"/></div>
        </div>
        <div className="flex justify-end gap-2">
          <Btn variant="secondary" onClick={onClose}>Vazgeç</Btn>
          <Btn onClick={save} disabled={busy} data-testid="sr-save">Kaydet</Btn>
        </div>
      </div>
    </Modal>
  );
}

function SupplierReturnDetailModal({ id, onClose, onChanged }) {
  const [doc, setDoc] = useState(null);
  const [edit, setEdit] = useState({});
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get(`/supplier-returns/${id}`).then(d => { setDoc(d); setEdit({}); }).catch(() => setDoc(null));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/supplier-returns/${id}`, edit);
      onChanged?.(); load();
    } catch (e) { alert(e.response?.data?.detail || "Kaydedilemedi"); }
    finally { setBusy(false); }
  };

  if (!doc) return <Modal open={true} onClose={onClose} title="Tedarikçi İadesi" size="lg"><div className="text-center py-8 text-slate-400">Yükleniyor...</div></Modal>;

  return (
    <Modal open={true} onClose={onClose} title={`Tedarikçi İadesi — ${doc.return_no}`} size="xl">
      <div className="space-y-4">
        <div className="grid md:grid-cols-3 gap-3 text-sm">
          <KV label="Durum" v={<StatusBadge status={doc.status}/>}/>
          <KV label="Tedarikçi" v={doc.supplier_name}/>
          <KV label="Malzeme" v={doc.material_name}/>
          <KV label="Lot" v={doc.lot_code}/>
          <KV label="Satın Alma No" v={doc.purchase_order_no}/>
          <KV label="Miktar" v={`${doc.qty_returned} ${doc.unit || ""}`}/>
          <KV label="Çözüm Tipi" v={doc.resolution_type === "refund" ? "İade" : "Yerine"}/>
          <KV label="İade Tarihi" v={fmtD(doc.return_date)}/>
          <KV label="Kaydı Alan" v={doc.created_by_name}/>
          <div className="md:col-span-3"><KV label="Neden" v={doc.reason}/></div>
          {doc.notes && <div className="md:col-span-3"><KV label="Notlar" v={doc.notes}/></div>}
        </div>

        <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 bg-slate-50/60 dark:bg-slate-800/40">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300 mb-2">Süreç Takibi</div>
          <div className="grid md:grid-cols-2 gap-3">
            <Select label="Durum" value={edit.status ?? doc.status} onChange={e => setEdit({ ...edit, status: e.target.value })} data-testid="sr-edit-status">
              <option value="pending">Beklemede</option>
              <option value="invoice_issued">Fatura Kesildi</option>
              <option value="refunded">İade Edildi</option>
              <option value="replaced">Yerine Gönderildi</option>
            </Select>
            <Input label="Fatura No" value={edit.invoice_no ?? doc.invoice_no ?? ""} onChange={e => setEdit({ ...edit, invoice_no: e.target.value })} data-testid="sr-edit-invoice"/>
            <Input label="Fatura Tarihi" type="date" value={edit.invoice_date ?? doc.invoice_date ?? ""} onChange={e => setEdit({ ...edit, invoice_date: e.target.value })} data-testid="sr-edit-invoice-date"/>
            <Input label="İade Tutarı" type="number" step="0.01" value={edit.refund_amount ?? doc.refund_amount ?? ""} onChange={e => setEdit({ ...edit, refund_amount: e.target.value ? Number(e.target.value) : null })} data-testid="sr-edit-refund"/>
            <Input label="Yerine Gelen Lot ID" value={edit.replacement_lot_id ?? doc.replacement_lot_id ?? ""} onChange={e => setEdit({ ...edit, replacement_lot_id: e.target.value })} data-testid="sr-edit-repl-lot"/>
            <Input label="Yerine Gelme Tarihi" type="date" value={edit.replacement_received_at ?? doc.replacement_received_at ?? ""} onChange={e => setEdit({ ...edit, replacement_received_at: e.target.value })} data-testid="sr-edit-repl-date"/>
          </div>
          <div className="flex justify-end mt-3">
            <Btn onClick={save} disabled={busy || Object.keys(edit).length === 0} data-testid="sr-edit-save">Kaydet</Btn>
          </div>
        </div>
      </div>
    </Modal>
  );
}
